// Pure decision logic for the V2 assignment actions menu — extracted so the branching (edit path,
// share links, and especially unassigning classes vs deleting the assignment) is unit-testable
// without rendering.
import constants from 'client/constants';

const assignmentResourceType = (isDraft: boolean): 'draft_assignments_v1' | 'assignments_v3' =>
  isDraft ? 'draft_assignments_v1' : 'assignments_v3';

export const getEditPath = (assignmentId: string, isDraft: boolean): string =>
  `/assignment-builder/create-assignment/${assignmentId}/settings${isDraft ? '/draft' : ''}`;

export const getShareUrl = (type: 'student' | 'educator', assignmentId: string): string =>
  type === 'student'
    ? `${constants.ALBERT_FULL_PATH}/assignment/${assignmentId}`
    : `${constants.ALBERT_FULL_PATH}/assignment-builder/create-assignment/${assignmentId}/settings`;

/** What a delete would take away, in the scope the teacher is deleting from. */
export interface AssignmentDeleteScope {
  /**
   * Classes this delete unassigns: the one class of a class-scoped list, or every class the acting
   * teacher teaches on the assignment in the cross-class ("All Classes") list.
   */
  classroomIds: string[];
  /** The acting teacher created the assignment, so it may be deleted outright. */
  isCreator: boolean;
  /**
   * Classes on the assignment across every teacher, including any the actor can't read. `null` when
   * it wasn't read — a class-scoped delete never deletes the assignment, so it doesn't need to know.
   */
  totalClassroomCount: number | null;
}

interface DeleteContext {
  assignmentId: string;
  isDraft: boolean;
  scope: AssignmentDeleteScope;
}

/**
 * Deleting the assignment itself is reserved for its creator, and only once unassigning would leave
 * no class on it — a co-teacher's class must survive the creator removing their own.
 */
const deletesAssignment = ({
  classroomIds,
  isCreator,
  totalClassroomCount
}: AssignmentDeleteScope): boolean =>
  isCreator && totalClassroomCount !== null && totalClassroomCount <= classroomIds.length;

interface DeleteRequest {
  resourcePath: string[];
  body: Record<string, unknown>;
}

/**
 * The one genericMandarkRequest a delete makes: the assignment (or draft) deleted outright, or its
 * in-scope classes unassigned so it survives for whoever else teaches it.
 *
 * `null` when there is nothing to do — the actor teaches none of the assignment's classes and can't
 * delete it outright.
 */
export const buildDeleteRequest = ({
  assignmentId,
  isDraft,
  scope
}: DeleteContext): DeleteRequest | null => {
  // A draft has no classes to unassign; it's only ever visible to its creator.
  if (isDraft) {
    return {resourcePath: [assignmentResourceType(true), assignmentId], body: {}};
  }
  if (deletesAssignment(scope)) {
    return {resourcePath: [assignmentResourceType(false), assignmentId], body: {}};
  }
  if (!scope.classroomIds.length) {
    return null;
  }
  return {
    resourcePath: ['assignments_v3', assignmentId, 'relationships', 'classrooms_v1'],
    body: {data: scope.classroomIds.map((id) => ({id, type: 'classrooms_v1'}))}
  };
};

export const getInvalidateResourcePath = (isDraft: boolean): string[] => [
  assignmentResourceType(isDraft)
];

export const getDeleteConfirmationCopy = ({
  assignmentName,
  isDraft,
  scope
}: {
  assignmentName: string;
  isDraft: boolean;
  scope: AssignmentDeleteScope;
}): string => {
  if (isDraft) {
    return 'Are you sure you want to delete this assignment?';
  }
  if (deletesAssignment(scope)) {
    return 'This will delete the assignment for all classes and students. This action cannot be undone.';
  }
  const classCopy =
    scope.classroomIds.length === 1 ? 'this class' : `your ${scope.classroomIds.length} classes`;
  return `You are about to remove ${classCopy} from ${assignmentName}. The assignment will still exist in any other classes it's assigned to. This action cannot be undone.`;
};
